import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import BrandRefreshTooltip from '../../../../components/CustomProductOption/BrandRefreshTooltip';
import { routeCountryPath } from '../../../../shared/utilities/common';
import { getGiftingProductCardImage } from './giftingCategoryMenuHelpers';
import rowStyles from './GiftingCategoryProductRowBrandRefresh.module.css';

// Touch/narrow viewports lack :hover; toggle .cardHoverActive for the same styles as desktop hover
const MOBILE_CARD_HOVER_MEDIA = '(max-width: 767px)';

function useMobileProductCardHover(cardRef) {
  const [usePointerHover, setUsePointerHover] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_CARD_HOVER_MEDIA);
    const update = () => {
      setUsePointerHover(mq.matches);
      if (!mq.matches) {
        setIsHovered(false);
      }
    };
    update();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  useEffect(() => {
    if (!usePointerHover) return undefined;

    const dismissTouchHover = (e) => {
      if (e.pointerType === 'mouse') return;
      const cardEl = cardRef.current;
      if (!cardEl || cardEl.contains(e.target)) return;
      setIsHovered(false);
    };

    document.addEventListener('pointerdown', dismissTouchHover, true);
    return () => document.removeEventListener('pointerdown', dismissTouchHover, true);
  }, [usePointerHover, cardRef]);

  const onMouseEnter = useCallback(() => {
    if (usePointerHover) {
      setIsHovered(true);
    }
  }, [usePointerHover]);

  const onMouseLeave = useCallback(() => {
    if (usePointerHover) {
      setIsHovered(false);
    }
  }, [usePointerHover]);

  const onPointerDown = useCallback(
    (e) => {
      if (!usePointerHover) return;
      if (e.pointerType === 'touch' || e.pointerType === 'pen') {
        setIsHovered(true);
      }
    },
    [usePointerHover]
  );

  return {
    isHovered: usePointerHover && isHovered,
    cardPointerHandlers: usePointerHover
      ? {
          onMouseEnter,
          onMouseLeave,
          onPointerDown,
        }
      : {},
  };
}

function useTruncatedProductCardTitleTooltip(text) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);
  const wrapperRef = useRef(null);

  const measureTruncation = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    measureTruncation();
  }, [text, measureTruncation]);

  useEffect(() => {
    window.addEventListener('resize', measureTruncation);
    return () => window.removeEventListener('resize', measureTruncation);
  }, [measureTruncation]);

  useEffect(() => {
    if (!showTooltip) return undefined;

    const onPointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showTooltip]);

  const toggleTooltip = useCallback(() => {
    setShowTooltip((prev) => !prev);
  }, []);

  return {
    textRef,
    wrapperRef,
    isTruncated,
    showTooltip,
    toggleTooltip,
  };
}

function GiftingCategoryProductCardTitle({ title }) {
  const { textRef, wrapperRef, isTruncated, showTooltip, toggleTooltip } =
    useTruncatedProductCardTitleTooltip(title);

  if (!title) {
    return null;
  }

  if (!isTruncated) {
    return (
      <h3 ref={textRef} className={rowStyles.cardTitle}>
        {title}
      </h3>
    );
  }

  return (
    <div ref={wrapperRef} className={rowStyles.titleWrapper}>
      <button
        type="button"
        className={rowStyles.descriptionButton}
        aria-label="Show full product name"
        aria-expanded={showTooltip}
        onClick={toggleTooltip}
      >
        <span ref={textRef} className={rowStyles.cardTitle}>
          {title}
        </span>
      </button>
      {showTooltip && (
        <div className={rowStyles.descriptionTooltipAnchor}>
          <BrandRefreshTooltip description={title} />
        </div>
      )}
    </div>
  );
}

GiftingCategoryProductCardTitle.propTypes = {
  title: PropTypes.string,
};

GiftingCategoryProductCardTitle.defaultProps = {
  title: '',
};

function GiftingCategoryProductCardDescription({ description }) {
  if (!description) {
    return <p className={rowStyles.cardDescription} aria-hidden="true" />;
  }

  return <p className={rowStyles.cardDescription}>{description}</p>;
}

GiftingCategoryProductCardDescription.propTypes = {
  description: PropTypes.string,
};

GiftingCategoryProductCardDescription.defaultProps = {
  description: '',
};

function GiftingCategoryProductCardBrandRefresh({ product }) {
  const href = product.productSlug
    ? routeCountryPath(`/products/${product.productSlug}`)
    : routeCountryPath(`/products/single/${product.id}`);
  const imageSrc = getGiftingProductCardImage(product);
  const cardRef = useRef(null);
  const { isHovered, cardPointerHandlers } = useMobileProductCardHover(cardRef);

  const cardClassName = isHovered
    ? `${rowStyles.card} ${rowStyles.cardHoverActive}`
    : rowStyles.card;

  return (
    <article
      ref={cardRef}
      className={cardClassName}
      data-part="category-product-card"
      onMouseEnter={cardPointerHandlers.onMouseEnter}
      onMouseLeave={cardPointerHandlers.onMouseLeave}
      onPointerDown={cardPointerHandlers.onPointerDown}
    >
      <div className={rowStyles.cardVisual} data-part="category-product-visual">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className={rowStyles.cardProductImage}
            draggable={false}
          />
        ) : null}
      </div>
      <div className={rowStyles.cardCopy} data-part="category-product-copy">
        <GiftingCategoryProductCardTitle title={product.title} />
        <GiftingCategoryProductCardDescription
          description={product.description}
        />
      </div>
      <Link to={href} className={rowStyles.orderButton} data-part="category-product-cta">
        Order Now
      </Link>
    </article>
  );
}

GiftingCategoryProductCardBrandRefresh.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    productSlug: PropTypes.string,
    productImage: PropTypes.string,
    productImageSmall: PropTypes.string,
  }).isRequired,
};

export default GiftingCategoryProductCardBrandRefresh;
export {
  GiftingCategoryProductCardDescription,
  GiftingCategoryProductCardTitle,
};
